import { Badge, Card, Group, Paper, Skeleton, Stack, Text, Title } from '@mantine/core'
import { IconCalendar, IconReceipt, IconUsers } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { StatInfoGraphic } from '@/components/StatInfoGraphic'
import { Table } from '@/components/Table'
import { useGlobalState } from '@/hooks/useGlobalState'
import { useGuestUserAccess } from '@/hooks/useGuestUserAccess'
import type { Invoice } from '@/services/api/api.types'
import { formatBillingDate } from '@/utils/analytics'
import { formatDollars, getSeatPrice } from '@/utils/planUtils'
import type { TableColumnDef } from '@/utils/table'

const noInvoices: Invoice[] = []

const formatInvoiceDate = (date: string | null | undefined): string => {
  if (!date) return '—'
  try {
    const datePart = date.split('T')[0]
    const [year, month, day] = datePart.split('-').map(Number)
    const localDate = new Date(year, month - 1, day)
    if (isNaN(localDate.getTime())) return '—'
    return localDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return '—'
  }
}

export function BillingTab() {
  const { api } = useGlobalState()

  const { data: billingInfo, isLoading } = useQuery({
    queryKey: ['billing', 'info'],
    queryFn: () => api.getBillingInfo(),
  })

  const { guestUserAccess } = useGuestUserAccess()

  const {
    data: plans,
    isPending: isPlansPending,
    isLoadingError: isPlansLoadingError,
  } = useQuery({
    queryKey: ['plans'],
    queryFn: () => api.getPlans(),
    staleTime: 0,
    refetchOnMount: 'always',
  })

  const { data: invoices, isLoading: isLoadingInvoices } = useQuery({
    queryKey: ['billing', 'invoices'],
    queryFn: () => api.getInvoices(),
  })

  const columns = useMemo<TableColumnDef<Invoice>[]>(
    () => [
      {
        accessorKey: 'billingDate',
        header: 'Billing Date',
        cell: ({ cell }) => formatInvoiceDate(cell.getValue<string>()),
      },
      {
        id: 'period',
        header: 'Period',
        cell: ({ row }) => {
          const start = formatInvoiceDate(row.original.periodStart)
          const end = formatInvoiceDate(row.original.periodEnd)
          return `${start} — ${end}`
        },
      },
      {
        accessorKey: 'userCount',
        header: 'Users',
      },
      {
        accessorKey: 'totalDollars',
        header: 'Amount',
        cell: ({ cell }) => `$${cell.getValue<number>().toFixed(2)}`,
      },
      {
        accessorKey: 'paid',
        header: 'Status',
        cell: ({ cell }) => (
          <Badge color={cell.getValue<boolean>() ? 'green' : 'orange'} variant="light">
            {cell.getValue<boolean>() ? 'Paid' : 'Unpaid'}
          </Badge>
        ),
      },
    ],
    [],
  )

  const info = billingInfo?.billingInfo
  // Without the plans file, billing info's own plan still names the per-user rate.
  const fallbackPrice = info?.plan?.priceDollars
  const seatPrice = isPlansLoadingError
    ? fallbackPrice != null && fallbackPrice > 0
      ? fallbackPrice
      : null
    : getSeatPrice(plans ?? [])

  return (
    <Card shadow="sm" p="xl" radius="md" withBorder>
      <Stack gap="xl" px="20px">
        <div>
          <Title order={4} mb="md">
            Billing Information
          </Title>

          {isLoading ? (
            <Stack gap="md">
              <Skeleton height={80} />
              <Skeleton height={120} />
            </Stack>
          ) : info ? (
            <Stack gap="xl">
              <Group justify="space-between">
                <StatInfoGraphic
                  icon={IconCalendar}
                  label="Next Billing Date"
                  value={formatBillingDate(info.nextBillingDate, true) || '—'}
                  tooltipText="Date when your next billing cycle begins"
                />
                <StatInfoGraphic
                  icon={IconReceipt}
                  label="Estimated Bill"
                  value={formatDollars(info.estimatedBill)}
                  tooltipText="Estimated amount for the current billing period, based on the most users subscribed at any point in it"
                />
                <StatInfoGraphic
                  icon={IconUsers}
                  label="Users"
                  value={(info.currentSubscribers ?? 0).toLocaleString()}
                  tooltipText="Users your channel pays for right now. Sidekick staff and users on a trial are not counted."
                />
              </Group>

              {isPlansPending ? (
                <Skeleton height={88} />
              ) : (
                <Paper p="lg" radius="md" withBorder>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>
                    Current Plan
                  </Text>
                  {seatPrice != null ? (
                    <Text size="lg" fw={600}>
                      {formatDollars(seatPrice)} per user / month
                    </Text>
                  ) : (
                    <Text size="sm" c="dimmed">
                      Plan details are unavailable right now.
                    </Text>
                  )}
                  {guestUserAccess && (
                    <Text size="sm" c="dimmed">
                      Guests are free
                    </Text>
                  )}
                </Paper>
              )}
            </Stack>
          ) : (
            <Text size="sm" c="dimmed">
              No billing information available.
            </Text>
          )}
        </div>

        <div style={{ paddingTop: 12, width: '100%' }}>
          <Title order={4} mb="md">
            Billing History
          </Title>
          <Table
            data={invoices ?? noInvoices}
            columns={columns}
            enableTopToolbar={false}
            enableGlobalFilter={false}
            striped
            isLoading={isLoadingInvoices}
          />
        </div>
      </Stack>
    </Card>
  )
}
